import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';

import ChevronRight from '@/assets/icons/chevronRight';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import { Text, TextProps } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { designSystem } from '@/styles/styles';
import { getLayoutAnimationsTransition } from '@/utils/animations/layout-animations-utils';

import { BetType, HYPOTHETICAL_STAKE } from './PickSlipBarContent';

const PayoutText = ({ text, color = ds.colorToken.text.primary }: { text: string; color?: TextProps['color'] }) => (
    <Text variant="bodyMedium" fontSize={14} lineHeight={20} color={color}>
        {text}
    </Text>
);

type PickSlipBarRightContentProps = {
    betType: BetType;
    hasOddsMaxedOutError: boolean;
    isSgpFetching: boolean;
    payout: number;
};

export const PickSlipBarRightContent = ({
    betType,
    hasOddsMaxedOutError,
    isSgpFetching,
    payout,
}: PickSlipBarRightContentProps) => {
    const { t } = useTranslation('betslip_sbk');
    const layoutTransition = getLayoutAnimationsTransition();

    return (
        <Animated.View layout={layoutTransition} style={styles.container}>
            {betType === 'GENERIC' ? (
                <PayoutText text={t('view_all_picks')} />
            ) : (
                <PayoutContent
                    hasOddsMaxedOutError={hasOddsMaxedOutError}
                    isSgpFetching={isSgpFetching}
                    payout={payout}
                />
            )}
            <ChevronRight />
        </Animated.View>
    );
};

type PayoutContentProps = {
    hasOddsMaxedOutError: boolean;
    isSgpFetching: boolean;
    payout: number;
};

const PayoutContent = ({ hasOddsMaxedOutError, isSgpFetching, payout }: PayoutContentProps) => {
    const { t } = useTranslation('betslip_sbk');
    const { formatCurrency } = useFormatCurrency();

    if (hasOddsMaxedOutError) {
        return <PayoutText text={t('odds_maxed_out')} color={ds.colorToken.text.warning} />;
    }

    if (isSgpFetching) {
        return (
            <LoadingSkeleton borderRadius="r4" skeletonColor={designSystem.colors.neutral300} width={98} height={20} />
        );
    }

    return (
        <>
            <SocialCurrencyActiveIcon />
            <AnimatedNumber
                prependText={t('payout_message', {
                    stake: HYPOTHETICAL_STAKE,
                    payout,
                })}
                displayFormat={value => formatCurrency(value)}
                value={payout}
                decimals={0}
                variant="bodyMedium"
                fontSize={14}
            />
        </>
    );
};

export const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
});
